import React, { useEffect, useRef } from "react"
import styled from "styled-components"
import Calendar from "react-calendar"
import dayjs from "dayjs"
import { Location, useFields } from "../App"

const CalendarWrapper = styled.div`
    max-width: 275px;

    .react-calendar__month-view__weekdays__weekday {
        text-align: center;
        width: 32px;
        height: 32px;
    }

    .react-calendar__tile {
        height: 39.28px;

        &:disabled {
            opacity: 0.35;
        }
    }

    .react-calendar__navigation {
        text-align: center;
        margin-bottom: 10px;
    }

    .react-calendar__navigation__prev-button {
        font-size: 24px;
        margin-right: 15px;
        position: relative;
        top: 2px;

        &:disabled {
            opacity: 0.35;
        }
    }

    .react-calendar__navigation__next-button {
        font-size: 24px;
        margin-left: 15px;
        position: relative;
        top: 2px;

        &:disabled {
            opacity: 0.35;
        }
    }

    .react-calendar__navigation__prev2-button,
    .react-calendar__navigation__next2-button {
        display: none;
    }

    .react-calendar__tile--active {
        background: #327fef;
        border-radius: 100%;
        color: #fff;
    }

    .react-calendar__month-view__days__day--neighboringMonth {
        opacity: 0.35;
    }
`

interface Props {
    location: Location
    minimumDays: number
    useTimeSlots: boolean
    blockedDates: { date: string }[]
}

export default function StepTwo({ location, minimumDays, useTimeSlots, blockedDates = [] }: Props) {
    const fields = useFields()
    const ref = useRef<HTMLDivElement>(null)
    React.useEffect(() => {
        fields.setLocation(location.title)
    }, [])

    const openinghours = JSON.parse(location.openinghours)

    let timeSlots: string[] = []

    if (fields.date) {
        const days = [
            "sunday",
            "monday",
            "tuesday",
            "wednesday",
            "thursday",
            "friday",
            "saturday",
        ]
        const key = days[new Date(fields.date).getDay()]
        const day = openinghours[key]

        const times: string[][] = day.value.map(
            (table: { start: Date; end: Date }) => {
                const maps: string[] = []
                let start = dayjs(new Date(table.start))
                const end = dayjs(new Date(table.end))

                while (start.toDate().getTime() <= end.toDate().getTime()) {
                    maps.push(start.format("HH:mm"))
                    start = start.add(15, "minutes")
                }

                return maps
            }
        )

        timeSlots = times.flat()
    }

    useEffect(() => {
        if (!fields.date) return
        if (!ref.current) return

        if (window.innerWidth < 640) {
            window.scrollTo({
                top: ref.current.offsetTop - 200,
                behavior: "smooth",
            })
        }
    }, [fields.date, ref.current])

    return (
        <div className="sm:mt-10">
            <h2 className="sm:text-2xl text-lg">Voorkeur datum en tijd</h2>
            <p className="mb-10">
                Selecteer de gewenste datum en tijd voor jouw afspraak. Wij
                nemen contact met je op om de afspraak te bevestigen. Let op, je
                afspraak moet eerst door ons bevestigd worden voordat deze
                definitief is.
            </p>

            <div className="grid lg:grid-cols-3 sm:grid-cols-2 gap-8">
                <CalendarWrapper className="sm:mr-auto mx-auto">
                    <Calendar
                        minDate={new Date()}
                        onChange={(val) => {
                            fields.setDate(val)
                            fields.setTime(null)
                        }}
                        value={fields.date}
                        tileDisabled={({ activeStartDate, date, view }) => {
                            const days = [
                                "sunday",
                                "monday",
                                "tuesday",
                                "wednesday",
                                "thursday",
                                "friday",
                                "saturday",
                            ]
                            const key = days[date.getDay()]
                            const day = openinghours[key]

                            if (day.value.length === 0) {
                                return true
                            }

                            const now = dayjs()

                            if (
                                !dayjs(date).isAfter(
                                    now.add(minimumDays, "day")
                                )
                            ) {
                                return true
                            }

                            if (blockedDates && blockedDates.find((blockedDate) => blockedDate.date === dayjs(date).format("DD/MM/YYYY"))) {
                                return true
                            }

                            return false
                        }}
                    />
                </CalendarWrapper>

                {fields.date && (
                    <div className="lg:col-span-2 max-w-md" ref={ref}>

                        {useTimeSlots && (
                            <>
                                <p className="mb-2">Tijdslot</p>

                                <select
                                    className="w-full"
                                    onChange={(e) => fields.setTime(e.target.value)}
                                >
                                    <option>Kies een tijdslot</option>
                                    {timeSlots.map((timeSlot) => (
                                        <option key={timeSlot}>{timeSlot}</option>
                                    ))}
                                </select>
                            </>
                        )}


                        <p className="mb-2 mt-4">Opmerking</p>

                        <textarea
                            className="w-full h-[120px]"
                            onChange={(e) =>
                                fields.setTimeComment(e.target.value)
                            }
                        ></textarea>
                    </div>
                )}
            </div>


            {fields.location && fields.date && (fields.time && useTimeSlots || !fields.time && !useTimeSlots) && (
                <div className="flex justify-end">
                    <button
                        onClick={() => fields.setStep(3)}
                        className="button primary mt-10 ml-auto"
                    >
                        Naar gegevens
                    </button>
                </div>
            )}
        </div>
    )
}
